import { useClerk, useSession, useUser } from '@clerk/clerk-react';
import { useCallback, useEffect, useRef } from 'react';

/**
 * Enforces single-session policy by signing out when another session becomes active.
 *
 * When the browser window regains focus, this hook checks if there are other
 * active sessions across ALL devices/browsers. If another session is more recent,
 * signs out the current session.
 *
 * Strategy: "Last session wins" - the most recently active session stays,
 * older sessions are signed out when they regain focus.
 *
 * Note: Uses clerk.user.getSessions() which returns ALL active sessions for the
 * user across all devices, not just the current browser.
 */
export const useSingleSessionEnforcement = () => {
  const { signOut } = useClerk();
  const { session: currentSession, isLoaded: isSessionLoaded } = useSession();
  const { user, isLoaded: isUserLoaded } = useUser();
  const isCheckingRef = useRef(false);

  const checkForConflictingSessions = useCallback(async () => {
    if (
      !isSessionLoaded ||
      !isUserLoaded ||
      !currentSession ||
      !user ||
      isCheckingRef.current
    ) {
      return;
    }

    isCheckingRef.current = true;

    try {
      // Get ALL active sessions for this user across all devices/browsers
      const allSessions = await user.getSessions();

      // Filter to only active sessions (exclude current session)
      const otherActiveSessions = allSessions.filter(
        (s) => s.id !== currentSession.id && s.status === 'active',
      );

      if (otherActiveSessions.length === 0) {
        return;
      }

      // Find the most recently active session among all sessions
      const currentSessionData = allSessions.find(
        (s) => s.id === currentSession.id,
      );
      if (!currentSessionData) {
        return;
      }

      const mostRecentSession = allSessions
        .filter((s) => s.status === 'active')
        .reduce((latest, session) => {
          const latestTime = latest.lastActiveAt
            ? new Date(latest.lastActiveAt).getTime()
            : 0;
          const sessionTime = session.lastActiveAt
            ? new Date(session.lastActiveAt).getTime()
            : 0;
          return sessionTime > latestTime ? session : latest;
        });

      // If current session is not the most recent, sign out
      if (mostRecentSession.id !== currentSession.id) {
        console.warn(
          '[Session] Another session is more recent. Signing out current session.',
          {
            currentSessionId: currentSession.id,
            mostRecentSessionId: mostRecentSession.id,
            currentLastActive: currentSessionData.lastActiveAt,
            mostRecentLastActive: mostRecentSession.lastActiveAt,
          },
        );

        // Store flag so we can show notification after they sign back in
        sessionStorage.setItem('session_conflict_signout', 'true');

        // Preserve current URL so user returns here after signing back in
        const signInUrl = new URL(process.env.REACT_CLERK_SIGN_IN_URL || '');
        signInUrl.searchParams.set('redirect_url', window.location.href);
        await signOut({ redirectUrl: signInUrl.toString() });
      }
    } catch (error) {
      console.error(
        '[Session] Error checking for conflicting sessions:',
        error,
      );
    } finally {
      isCheckingRef.current = false;
    }
  }, [isSessionLoaded, isUserLoaded, currentSession, user, signOut]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkForConflictingSessions();
      }
    };

    const handleFocus = () => {
      checkForConflictingSessions();
    };

    // Listen for visibility changes (tab switching, minimizing)
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Listen for window focus (switching between windows)
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [checkForConflictingSessions]);
};
